import { type FC, useEffect, useState } from "react";
import { Select, type SelectOptionType } from "@onshopfront/components";
import { useShopfrontDispatch } from "../../_Bootstrap/hooks";
import { hideDialog } from "../../Actions/Dialogs/DialogActions";
import Button from "../../Common/Buttons/Button";
import { determineDataSource } from "../../Data/DataStores/DataSource";

interface Props {
    onConfirm: (users: Array<string>, height: null | number) => void;
    users: Array<string>;
    lowest: number;
    height: null | number;
}

export const DashboardConfigure: FC<Props> = ({
    users,
    lowest,
    height: defaultHeight,
    onConfirm,
}) => {
    const dispatch = useShopfrontDispatch();

    const [ height, setHeight ] = useState(() => {
        return (defaultHeight === null ? lowest + 1 : Math.max(defaultHeight, lowest + 1)).toString();
    });

    const [ loading, setLoading ] = useState(true);
    const [ selected, setSelected ] = useState<Array<SelectOptionType<string>>>([]);
    const [ options, setOptions ] = useState<Array<SelectOptionType<string>>>([]);

    useEffect(() => {
        setLoading(true);

        determineDataSource()
            .then(async source => {
                const dbUsers = await source.users.all();

                const selected: Array<SelectOptionType<string>> = [];
                const options: Array<SelectOptionType<string>> = [];

                for(const user of dbUsers) {
                    const option = {
                        label: user.name,
                        value: user.uuid,
                    };

                    options.push(option);

                    if(users.includes(user.uuid)) {
                        selected.push(option);
                    }
                }

                setSelected(selected);
                setOptions(options);
                setLoading(false);
            });
        // Intentionally not adding "users" because we only want to use the values for initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <p>Select additional users to save this dashboard to</p>
            <Select
                value={selected}
                options={options}
                isMulti
                isClearable
                loading={loading}
                onChange={(options: null | Array<SelectOptionType<string>>) => {
                    if(options === null) {
                        setSelected([]);

                        return;
                    }

                    setSelected(options);
                }}
                placeholder="Select Additional Users"
            />
            <label className="block mt-2">
                Dashboard Height (in rows)
                <input
                    type="number"
                    step="1"
                    min={lowest + 1}
                    value={height}
                    onChange={e => {
                        setHeight(e.currentTarget.value);
                    }}
                />
            </label>
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => {
                        dispatch(hideDialog());
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={() => {
                        dispatch(hideDialog());

                        let heightValue: number | null = null;

                        const parsed = parseFloat(height);

                        if(!isNaN(parsed)) {
                            if(parsed !== lowest + 1) {
                                heightValue = parsed;
                            }
                        }

                        onConfirm(selected.map(option => option.value), heightValue);
                    }}
                >
                    Continue
                </Button>
            </div>
        </>
    );
};
